import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Vet from "@/pages/Vet";
import Milk from "@/pages/Milk";
import Finance from "@/pages/Finance";
import Stock from "@/pages/Stock";
import CalfFeeding from "@/pages/CalfFeeding";
import Medicines from "@/pages/Medicines";
import Fields from "@/pages/Fields";
import Rations from "@/pages/Rations";
import HerdStatus from "@/pages/HerdStatus";
import Comparison from "@/pages/Comparison";
import AIPage from "@/pages/AI";
import UserManagement from "@/pages/UserManagement";
import Backup from "@/pages/Backup";
import AuditLog from "@/pages/AuditLog";
import "@/App.css";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-10 text-[#8a938c]">Yükleniyor...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function PageGuard({ pageKey, children }) {
  const { user } = useAuth();
  if (!user) return null;
  const allowed = Array.isArray(user.allowed_pages) ? user.allowed_pages : [];
  if (!allowed.includes(pageKey)) {
    return (
      <div className="p-10 text-center" data-testid="page-forbidden">
        <div className="text-2xl mb-2">🔒</div>
        <div className="text-lg font-semibold text-[#2D4A36]">Bu sayfaya erişim yetkiniz yok</div>
        <div className="text-sm text-[#8a938c] mt-1">Yetki için işletme sahibiyle iletişime geçin.</div>
      </div>
    );
  }
  return children;
}

function OwnerOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "owner") {
    return <Navigate to="/" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><AppShell /></Protected>}>
        <Route index element={<PageGuard pageKey="panel"><Dashboard /></PageGuard>} />
        <Route path="veteriner" element={<PageGuard pageKey="veteriner"><Vet /></PageGuard>} />
        <Route path="ilaclar" element={<PageGuard pageKey="ilaclar"><Medicines /></PageGuard>} />
        <Route path="sut" element={<PageGuard pageKey="sut"><Milk /></PageGuard>} />
        <Route path="stok" element={<PageGuard pageKey="stok"><Stock /></PageGuard>} />
        <Route path="buzagi" element={<PageGuard pageKey="buzagi"><CalfFeeding /></PageGuard>} />
        <Route path="tarla" element={<PageGuard pageKey="tarla"><Fields /></PageGuard>} />
        <Route path="rasyon" element={<PageGuard pageKey="rasyon"><Rations /></PageGuard>} />
        <Route path="suru" element={<PageGuard pageKey="suru"><HerdStatus /></PageGuard>} />
        <Route path="finans" element={<PageGuard pageKey="finans"><Finance /></PageGuard>} />
        <Route path="karsilastirma" element={<PageGuard pageKey="karsilastirma"><Comparison /></PageGuard>} />
        <Route path="ai" element={<PageGuard pageKey="ai"><AIPage /></PageGuard>} />
        <Route path="kullanicilar" element={<OwnerOnly><UserManagement /></OwnerOnly>} />
        <Route path="yedekleme" element={<OwnerOnly><Backup /></OwnerOnly>} />
        <Route path="denetim" element={<OwnerOnly><AuditLog /></OwnerOnly>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
